/**
 * Storage utilities for managing local storage
 */

/**
 * User pool types
 */
export enum USER_TYPE {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
}

/**
 * Storage keys
 */
const STORAGE_KEYS = {
  POOL_ID: 'poolId',
  USER_PREFERENCES: 'userPreferences',
  SESSION_DATA: 'sessionData',
  LAST_ACTIVITY: 'lastActivity',
} as const;

/**
 * Set the poolId in localStorage
 * @param poolId - The pool identifier (INTERNAL or EXTERNAL)
 * @returns boolean - Success status
 */
export function setPool(poolId: string): boolean {
  try {
    // Encode the poolId for basic obfuscation
    localStorage.setItem(STORAGE_KEYS.POOL_ID, btoa(poolId));
    return true;
  } catch (error) {
    console.error('Error setting pool in storage:', error);
    return false;
  }
}

/**
 * Get the poolId from localStorage
 * @returns string | null - The decoded pool ID or null
 */
export function getPool(): string | null {
  try {
    const encoded = localStorage.getItem(STORAGE_KEYS.POOL_ID);
    return encoded ? atob(encoded) : null;
  } catch (error) {
    console.error('Error reading pool from storage:', error);
    return null;
  }
}

/**
 * Clear the poolId and any chat/session history from localStorage
 */
export function clearPoolAndChatHistory(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.POOL_ID);
    localStorage.removeItem(STORAGE_KEYS.SESSION_DATA);
    // Also clear any legacy keys that might exist
    localStorage.removeItem('mockUser');
    localStorage.removeItem('authToken');
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
}

/**
 * Set user preferences in localStorage
 * @param preferences - User preferences object
 * @returns boolean - Success status
 */
export function setUserPreferences(preferences: Record<string, any>): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error('Error setting user preferences:', error);
    return false;
  }
}

/**
 * Get user preferences from localStorage
 * @returns Record<string, any> | null - User preferences or null
 */
export function getUserPreferences(): Record<string, any> | null {
  try {
    const preferences = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES);
    return preferences ? JSON.parse(preferences) : null;
  } catch (error) {
    console.error('Error getting user preferences:', error);
    return null;
  }
}

/**
 * Set session data in localStorage
 * @param data - Session data object
 * @returns boolean - Success status
 */
export function setSessionData(data: Record<string, any>): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.SESSION_DATA, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error setting session data:', error);
    return false;
  }
}

/**
 * Get session data from localStorage
 * @returns Record<string, any> | null - Session data or null
 */
export function getSessionData(): Record<string, any> | null {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SESSION_DATA);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error getting session data:', error);
    return null;
  }
}

/**
 * Update last activity timestamp
 * @returns boolean - Success status
 */
export function updateLastActivity(): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, new Date().toISOString());
    return true;
  } catch (error) {
    console.error('Error updating last activity:', error);
    return false;
  }
}

/**
 * Get last activity timestamp
 * @returns Date | null - Last activity date or null
 */
export function getLastActivity(): Date | null {
  try {
    const timestamp = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
    if (!timestamp) {
      return null;
    }
    const date = new Date(timestamp);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date format in last activity:', timestamp);
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error getting last activity:', error);
    return null;
  }
}

/**
 * Check if session is expired based on inactivity
 * @param maxInactivityMinutes - Maximum inactivity time in minutes (default: 60)
 * @returns boolean - True if session is expired
 */
export function isSessionExpired(maxInactivityMinutes: number = 60): boolean {
  const lastActivity = getLastActivity();
  if (!lastActivity) {
    return true;
  }

  const now = new Date();
  const diffMinutes = (now.getTime() - lastActivity.getTime()) / (1000 * 60);
  return diffMinutes > maxInactivityMinutes;
}

/**
 * Clear all authentication-related data from localStorage
 */
export function clearAllAuthData(): void {
  try {
    // Clear all our storage keys
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    // Clear any Amplify-related keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('aws') || key.includes('cognito') || key.includes('amplify'))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing all auth data:', error);
  }
}

/**
 * Storage utility to safely get item with JSON parse
 * @param key - Storage key
 * @returns T | null - Parsed value or null
 */
export function getStorageItem<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error getting storage item ${key}:`, error);
    return null;
  }
}

/**
 * Storage utility to safely set item with JSON stringify
 * @param key - Storage key
 * @param value - Value to store
 * @returns boolean - Success status
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error setting storage item ${key}:`, error);
    return false;
  }
}
